import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <article className="group flex flex-col sm:flex-row gap-4 bg-background-50 rounded-lg border border-secondary-200/60 hover:border-primary-200 transition-all duration-300 overflow-hidden">
        <Link to={`/article/${article.id}`} className="block w-full sm:w-[200px] shrink-0 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-36 sm:h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
        <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                {article.category}
              </span>
              {article.isHot && (
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                  热门
                </span>
              )}
              <span className="text-foreground-400 text-xs">{article.publishedAt}</span>
            </div>
            <Link to={`/article/${article.id}`} className="block">
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                {article.title}
              </h3>
            </Link>
            <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
            <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万</span>
            <span><i className="ri-user-line mr-1"></i>{article.author}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group flex gap-3 py-3 border-b border-secondary-100">
        <div className="flex-1 min-w-0">
          <Link to={`/article/${article.id}`} className="block">
            <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
              {article.title}
            </h4>
          </Link>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-400">
            <span>{article.category}</span>
            <span>·</span>
            <span>{article.publishedAt}</span>
          </div>
        </div>
        <Link to={`/article/${article.id}`} className="block w-20 h-16 shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </Link>
      </article>
    );
  }

  // default card
  return (
    <article className="group bg-background-50 rounded-lg border border-secondary-200/60 hover:border-primary-200 transition-all duration-300 overflow-hidden flex flex-col">
      <Link to={`/article/${article.id}`} className="block overflow-hidden">
        <div className="relative w-full h-48 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {article.isFeatured && (
            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium whitespace-nowrap">
              头条
            </div>
          )}
        </div>
      </Link>
      <div className="flex-1 p-4 flex flex-col">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs text-primary-600 font-medium whitespace-nowrap">{article.category}</span>
          <span className="text-xs text-foreground-400">{article.publishedAt}</span>
        </div>
        <Link to={`/article/${article.id}`} className="block flex-1">
          <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400 pt-3 border-t border-secondary-100">
          <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(1)}万</span>
          <span>{article.author}</span>
        </div>
      </div>
    </article>
  );
}